import { Check, Images, ImageOff, MapPin } from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatDate, formatNumber } from '@/lib/format-es';
import { resolveMediaUrl } from '@/lib/media-url';
import { cn } from '@/lib/utils';
import { speciesLabel } from '../captureExportModel';
import type { CaptureExportRow } from '../types';

function measures(row: CaptureExportRow): string {
    const parts = [
        row.weightKg != null ? `${formatNumber(row.weightKg)} kg` : null,
        row.lengthCm != null ? `${formatNumber(row.lengthCm)} cm` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'Sin peso ni talla';
}

export function CaptureCard({
    row,
    selected,
    onToggle,
}: {
    row: CaptureExportRow;
    selected: boolean;
    onToggle: () => void;
}) {
    const thumb = resolveMediaUrl(row.photos[0]?.path);
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-pressed={selected}
            className={cn(
                'group relative flex flex-col overflow-hidden rounded-xl border bg-card text-left transition-colors',
                selected ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-primary/50',
            )}
        >
            <div className="relative aspect-square bg-muted">
                {thumb ? (
                    <img src={thumb} alt={speciesLabel(row)} loading="lazy" className="h-full w-full object-cover" />
                ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                        <ImageOff size={24} />
                    </div>
                )}
                <span
                    className={cn(
                        'absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-md border',
                        selected ? 'border-primary bg-primary text-primary-foreground' : 'border-white/70 bg-black/40 text-transparent',
                    )}
                >
                    <Check size={14} />
                </span>
                <div className="absolute right-2 top-2">
                    <StatusBadge
                        status={row.isPublic ? 'public' : 'private'}
                        label={row.isPublic ? 'Pública' : 'Privada'}
                        variant={row.isPublic ? 'success' : 'warning'}
                    />
                </div>
                {row.photos.length > 1 && (
                    <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
                        <Images size={12} />
                        {row.photos.length}
                    </span>
                )}
            </div>
            <div className="space-y-0.5 p-2.5">
                <p className="truncate text-sm font-semibold text-foreground">{speciesLabel(row)}</p>
                <p className="truncate text-xs text-foreground/80">{measures(row)}</p>
                <p className="truncate text-xs text-muted-foreground">{row.baitLure || 'Sin señuelo indicado'}</p>
                <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                    <MapPin size={11} className="shrink-0" />
                    <span className="truncate">
                        {row.distanceKm != null ? `${formatNumber(row.distanceKm)} km · ` : ''}
                        {row.location || 'Sin zona'}
                    </span>
                </p>
                <p className="text-[11px] text-muted-foreground">
                    {formatDate(row.captureDate)} · {formatNumber(row.likesCount)} likes
                </p>
            </div>
        </button>
    );
}
